/**
 * Open alerts — the single definition every screen counts with.
 *
 * Open = status in OPEN_ALERT_STATUSES, with no created_at window: an alert
 * opened months ago and still unresolved is still open ("Ahora", not "30d").
 * Crítica = open AND potential_impact = 'Alto' (product decision 2026-10-08;
 * category 'Malicioso' describes intent, not severity).
 *
 * SECURITY: every query filters tenant_id explicitly (defense in depth with RLS).
 */
import { supabase } from '@/services/supabase';

export const OPEN_ALERT_STATUSES = ['new', 'open', 'in_progress', 'investigating', 'escalated', 'acknowledged'] as const;
export const CRITICAL_IMPACT = 'Alto';

export function isAlertOpen(status: string | null | undefined): boolean {
  return !!status && (OPEN_ALERT_STATUSES as readonly string[]).includes(status);
}

/** Accepts DB rows (potential_impact) and app-side Alert objects (potentialImpact). */
export function isAlertCritical(alert: { status?: string | null; potential_impact?: string | null; potentialImpact?: string | null }): boolean {
  return isAlertOpen(alert.status) && (alert.potential_impact ?? alert.potentialImpact) === CRITICAL_IMPACT;
}

type Client = Pick<typeof supabase, 'from'>;

export async function fetchOpenAlertCounts(
  tenantId: string,
  scope: { storeIds?: string[] } = {},
  client: Client = supabase,
): Promise<{ open: number; critical: number; fresh: number }> {
  if (!tenantId) return { open: 0, critical: 0, fresh: 0 };
  const base = () => {
    let q = client
      .from('alerts')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .in('status', [...OPEN_ALERT_STATUSES]);
    if (scope.storeIds && scope.storeIds.length > 0) q = q.in('store_id', scope.storeIds);
    return q;
  };
  const [openRes, criticalRes, freshRes] = await Promise.all([
    base(),
    base().eq('potential_impact', CRITICAL_IMPACT),
    base().eq('status', 'new'),
  ]);
  // Throw so React Query shows an error: a failed count is not "0 open alerts".
  for (const res of [openRes, criticalRes, freshRes]) if (res.error) throw res.error;
  return { open: openRes.count ?? 0, critical: criticalRes.count ?? 0, fresh: freshRes.count ?? 0 };
}

export interface UrgentAlert {
  id: string;
  description: string | null;
  type: string | null;
  store_id: string | null;
  created_at: string;
  /** CLP — the money at stake. potential_impact is the Bajo/Medio/Alto label. */
  estimated_value: number | null;
}

/**
 * Open critical alerts ranked by money at stake: the top 3 to act on, and the
 * total exposure across every open critical alert.
 */
export async function fetchUrgentAlerts(
  tenantId: string,
  client: Client = supabase,
  limit = 3,
): Promise<{ top: UrgentAlert[]; exposure: number }> {
  if (!tenantId) return { top: [], exposure: 0 };
  const critical = (columns: string) =>
    client
      .from('alerts')
      .select(columns)
      .eq('tenant_id', tenantId)
      .in('status', [...OPEN_ALERT_STATUSES])
      .eq('potential_impact', CRITICAL_IMPACT);
  const [topRes, valuesRes] = await Promise.all([
    critical('id, description, type, store_id, created_at, estimated_value')
      .order('estimated_value', { ascending: false, nullsFirst: false })
      .limit(limit),
    critical('estimated_value'),
  ]);
  // A failed query is not "no critical alerts" / $0 exposure.
  if (topRes.error) throw topRes.error;
  if (valuesRes.error) throw valuesRes.error;
  const values = (valuesRes.data ?? []) as unknown as Array<{ estimated_value: number | string | null }>;
  const exposure = values.reduce((sum, r) => sum + (Number(r.estimated_value) || 0), 0);
  return { top: (topRes.data ?? []) as unknown as UrgentAlert[], exposure };
}
